import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter, Link as RouterLink } from "react-router-dom";
import { Box, CircularProgress, Grid, Typography } from "@material-ui/core";
import { withTranslation } from "react-i18next";
import { Bar, Line } from "react-chartjs-2";
import styled from "styled-components";
import BackButton from "../../components/BackButton";
import Group from "../../waybee-ui/Group";
import Heading from "../../waybee-ui/Heading/Heading";
import Divider from "../../waybee-ui/Divider";
import { buildDemoClassOverview } from "../../demo/buildDemoClassOverview";
import AiIcon from "./components/AiIcon";

const LoadContainer = styled.div`
  text-align: center;
  margin: 24px;
`;

const KpiRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 8px 0 20px;
`;

const Kpi = styled.div`
  flex: 1 1 140px;
  min-width: 120px;
  padding: 12px 14px;
  border-left: 4px solid
    ${({ tone, theme }) => {
      if (tone === "alert") return theme.color.error.main;
      if (tone === "warn") return "#c9a227";
      return theme.color.primary.main;
    }};
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.color.primary.light}33 0%,
    #ffffff 70%
  );
`;

const KpiValue = styled.div`
  font-size: 28px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.secondary.dark};
  line-height: 1.1;
`;

const KpiLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;
  color: ${({ theme }) => theme.color.secondary.main};
`;

const KpiHint = styled.div`
  font-size: 12px;
  margin-top: 2px;
  color: ${({ theme }) => theme.color.default.main};
`;

const SectionTitle = styled(Typography)`
  && {
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.color.secondary.dark};
    margin: 8px 0 12px;
  }
`;

const ChartBox = styled.div`
  height: 220px;
  margin-bottom: 8px;
`;

const AttentionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const AttentionItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 48, 59, 0.08);
  &:last-child {
    border-bottom: none;
  }
`;

const Severity = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
  background: ${({ severity, theme }) => {
    if (severity === "high") return theme.color.error.main;
    if (severity === "medium") return "#c9a227";
    return theme.color.primary.main;
  }};
`;

const StudentLink = styled(RouterLink)`
  color: ${({ theme }) => theme.color.secondary.dark};
  font-weight: 600;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.color.primary.dark};
    text-decoration: underline;
  }
`;

const InsightsPanel = styled.section`
  margin-top: 8px;
  padding: 16px 18px 18px;
  border-radius: 10px;
  background: linear-gradient(
    125deg,
    ${({ theme }) => theme.color.secondary.dark} 0%,
    ${({ theme }) => theme.color.secondary.main} 55%,
    #1a5c6e 100%
  );
  color: #ffffff;
`;

const InsightsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

const InsightsTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

const InsightCard = styled.article`
  padding: 12px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.18);
  &:first-of-type {
    border-top: none;
    padding-top: 0;
  }
`;

const InsightName = styled.h3`
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 600;
`;

const InsightBody = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  opacity: 0.92;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const Tag = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
`;

const Footnote = styled.p`
  margin: 12px 0 0;
  font-size: 11px;
  opacity: 0.65;
`;

const chartOptions = {
  maintainAspectRatio: false,
  legend: { display: false },
  scales: {
    yAxes: [
      {
        ticks: { beginAtZero: true, max: 100 },
        gridLines: { color: "rgba(0,48,59,0.06)" }
      }
    ],
    xAxes: [{ gridLines: { display: false } }]
  }
};

const barOptions = {
  maintainAspectRatio: false,
  legend: { display: false },
  scales: {
    yAxes: [
      {
        ticks: { beginAtZero: true, precision: 0 },
        gridLines: { color: "rgba(0,48,59,0.06)" }
      }
    ],
    xAxes: [{ gridLines: { display: false } }]
  }
};

class ClassOverview extends Component {
  constructor(props) {
    super(props);
    this.state = { data: null, loading: true };
  }

  componentDidMount() {
    const { match } = this.props;
    const classId = match.params.id;
    // Demo: sync mock; depois pode vir de API.
    const data = buildDemoClassOverview(classId);
    this.setState({ data, loading: false });
  }

  render() {
    const { match, t } = this.props;
    const { data, loading } = this.state;
    const classId = match.params.id;

    if (loading || !data) {
      return (
        <LoadContainer>
          <CircularProgress />
        </LoadContainer>
      );
    }

    return (
      <Grid container spacing={2}>
        <BackButton to={`/classes/${classId}`} />
        <Grid item xs={12}>
          <Group>
            <Heading level={1} icon="equalizer" gutterBottom>
              {t("classes.overview.title")}
            </Heading>
            <Typography variant="body2" color="textSecondary">
              {data.className}
              {" · "}
              {data.periodLabel}
            </Typography>
            <Divider />

            <SectionTitle>{t("classes.overview.health")}</SectionTitle>
            <KpiRow>
              {data.kpis.map(k => (
                <Kpi key={k.id} tone={k.tone}>
                  <KpiValue>{k.value}</KpiValue>
                  <KpiLabel>{k.label}</KpiLabel>
                  <KpiHint>{k.hint}</KpiHint>
                </Kpi>
              ))}
            </KpiRow>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <SectionTitle>
                  {t("classes.overview.attendanceTrend")}
                </SectionTitle>
                <ChartBox>
                  <Line data={data.attendanceTrend} options={chartOptions} />
                </ChartBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <SectionTitle>
                  {t("classes.overview.occurrencesByType")}
                </SectionTitle>
                <ChartBox>
                  <Bar data={data.occurrencesByType} options={barOptions} />
                </ChartBox>
              </Grid>
            </Grid>

            <SectionTitle>
              {t("classes.overview.attentionStudents")}
            </SectionTitle>
            <AttentionList>
              {data.attentionStudents.map(s => (
                <AttentionItem key={s.ra}>
                  <Severity severity={s.severity} />
                  <Box>
                    <StudentLink to={`/student/${s.ra}`}>{s.name}</StudentLink>
                    <Typography variant="body2" color="textSecondary">
                      {s.reason}
                    </Typography>
                  </Box>
                </AttentionItem>
              ))}
            </AttentionList>

            <Box mt={3}>
              <InsightsPanel aria-label={t("classes.overview.insightsAria")}>
                <InsightsHeader>
                  <AiIcon size={26} color="#ffffff" />
                  <InsightsTitle>
                    {t("classes.overview.insights")}
                  </InsightsTitle>
                </InsightsHeader>
                {data.insights.map(insight => (
                  <InsightCard key={insight.id}>
                    <InsightName>{insight.title}</InsightName>
                    <InsightBody>{insight.body}</InsightBody>
                    <TagRow>
                      {insight.tags.map(tag => (
                        <Tag key={tag}>{tag}</Tag>
                      ))}
                    </TagRow>
                  </InsightCard>
                ))}
                <Footnote>{t("classes.overview.insightsFootnote")}</Footnote>
              </InsightsPanel>
            </Box>
          </Group>
        </Grid>
      </Grid>
    );
  }
}

ClassOverview.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({ id: PropTypes.string })
  }).isRequired,
  t: PropTypes.func.isRequired
};

export default withRouter(withTranslation()(ClassOverview));
